import type { ActivityDay, StreakData } from '@/types/gamification.types';

/**
 * StreakService
 * Calculates user activity streaks based on daily story completion
 *
 * Algorithm:
 * 1. Sort activity days chronologically
 * 2. Calculate current streak (consecutive days from most recent)
 * 3. Calculate longest streak (max consecutive days in history)
 * 4. Determine if streak is at risk (no activity today)
 *
 * Streak Rules:
 * - Un día cuenta si hay actividad en el calendario (historias, recursos, repaso, biblioteca)
 * - Consecutive days = no gaps between activity dates
 * - Current streak includes today OR yesterday (grace period)
 * - Streak breaks if more than 1 day passes without activity
 */

/**
 * Normalize date to start of day (00:00:00) in local timezone
 * Removes time component for consistent date comparison
 */
function normalizeDate(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

/**
 * Convert ISO date string (YYYY-MM-DD) to normalized Date object in local time.
 * Using new Date("YYYY-MM-DD") would parse as UTC midnight, shifting the date
 * back 1 day for users in UTC+ timezones. We parse manually to stay in local time.
 */
function parseDate(dateString: string): Date {
  const parts = dateString.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];

  // Validate that all parts are valid numbers
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Invalid date string format: ${dateString}`);
  }

  return normalizeDate(new Date(year, month - 1, day));
}

/**
 * Get difference in days between two dates
 */
function getDaysDifference(date1: Date, date2: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  const diff = Math.abs(date1.getTime() - date2.getTime());
  return Math.floor(diff / msPerDay);
}

/**
 * Check if two dates are consecutive (differ by exactly 1 day)
 */
function areConsecutiveDays(date1: Date, date2: Date): boolean {
  return getDaysDifference(date1, date2) === 1;
}

/**
 * Check if a date is today
 */
function isToday(date: Date): boolean {
  const today = normalizeDate(new Date());
  return date.getTime() === today.getTime();
}

/**
 * Check if a date is yesterday
 */
function isYesterday(date: Date): boolean {
  const yesterday = normalizeDate(new Date());
  yesterday.setDate(yesterday.getDate() - 1);
  return date.getTime() === yesterday.getTime();
}

/**
 * Calculate user's activity streak
 *
 * @param activities - Array of activity days (must include hasActivity flag)
 * @returns Streak data with current streak, longest streak, and risk status
 *
 * @example
 * ```typescript
 * const activities: ActivityDay[] = [
 *   { date: '2025-01-01', hasActivity: true },
 *   { date: '2025-01-02', hasActivity: true },
 *   { date: '2025-01-03', hasActivity: true },
 *   { date: '2025-01-05', hasActivity: true }, // Gap - breaks streak
 * ];
 *
 * const streak = calculateStreak(activities);
 * // Result:
 * // - currentStreak: 1 (only Jan 5)
 * // - longestStreak: 3 (Jan 1-3)
 * // - isStreakAtRisk: true (if today > Jan 5)
 * ```
 */
export function calculateStreak(activities: ActivityDay[]): StreakData {
  // Handle empty activities
  if (!activities || activities.length === 0) {
    return {
      currentStreak: 0,
      longestStreak: 0,
      totalActiveDays: 0,
      streakStartDate: null,
      lastActivityDate: null,
      hasActivityToday: false,
      isStreakAtRisk: false,
    };
  }

  // Filter only days with activity and sort chronologically
  const activeDays = activities
    .filter((day) => day.hasActivity)
    .map((day) => ({
      ...day,
      dateObj: parseDate(day.date),
    }))
    .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());

  // No active days
  if (activeDays.length === 0) {
    return {
      currentStreak: 0,
      longestStreak: 0,
      totalActiveDays: 0,
      streakStartDate: null,
      lastActivityDate: null,
      hasActivityToday: false,
      isStreakAtRisk: false,
    };
  }

  const lastActivity = activeDays[activeDays.length - 1]!;
  const hasActivityToday = isToday(lastActivity.dateObj);

  // Calculate current streak
  // Start from most recent day and work backwards
  let currentStreak = 0;
  let streakStartDate: Date | null = null;

  // Current streak must include today or yesterday (grace period)
  if (hasActivityToday || isYesterday(lastActivity.dateObj)) {
    currentStreak = 1;
    streakStartDate = lastActivity.dateObj;

    // Walk backwards through days to count consecutive streak
    for (let i = activeDays.length - 2; i >= 0; i--) {
      const currentDay = activeDays[i]!;
      const nextDay = activeDays[i + 1]!;

      if (areConsecutiveDays(currentDay.dateObj, nextDay.dateObj)) {
        currentStreak++;
        streakStartDate = currentDay.dateObj;
      } else {
        // Gap found - stop counting
        break;
      }
    }
  }

  // Calculate longest streak in history
  let longestStreak = 0;
  let tempStreak = 1;

  for (let i = 1; i < activeDays.length; i++) {
    const prevDay = activeDays[i - 1]!;
    const currentDay = activeDays[i]!;

    if (areConsecutiveDays(prevDay.dateObj, currentDay.dateObj)) {
      tempStreak++;
      longestStreak = Math.max(longestStreak, tempStreak);
    } else {
      // Gap found - reset temp streak
      tempStreak = 1;
    }
  }

  // Handle case where there's only 1 active day
  longestStreak = Math.max(longestStreak, 1);

  // Determine if streak is at risk
  // Risk = no activity today and current streak > 0
  const isStreakAtRisk = !hasActivityToday && currentStreak > 0;

  return {
    currentStreak,
    longestStreak: Math.max(longestStreak, currentStreak),
    totalActiveDays: activeDays.length,
    streakStartDate: streakStartDate ? streakStartDate.toISOString() : null,
    lastActivityDate: lastActivity.date,
    hasActivityToday,
    isStreakAtRisk,
  };
}

/**
 * Generate mock activity data for testing/development
 * Creates consecutive days with occasional gaps
 */
export function generateMockActivities(days: number = 30): ActivityDay[] {
  const activities: ActivityDay[] = [];
  const today = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);

    // Simulate 80% activity rate with occasional gaps
    const hasActivity = Math.random() > 0.2;

    activities.push({
      date: date.toISOString(),
      hasActivity,
      storiesCompleted: hasActivity ? Math.floor(Math.random() * 3) + 1 : 0,
      xpEarned: hasActivity ? Math.floor(Math.random() * 100) + 50 : 0,
    });
  }

  return activities;
}
